import { commonActions, apiActions } from '../app/actions';
import { fireMessageEvent } from '../messenger/utils';
import { makeRequest } from '../lib/fetch';
import { setNotificationBarToOpen } from '../ui/actions';
import { batch } from 'react-redux';
import { analyticActionMap } from '../analytics/utils';

const { skillTestAnswered, path, answerElement, correctValue, incorrectValue } = analyticActionMap;

const checkIfPlayoffs = async (config) => {
  const { apiUrl, userId, env, currentSession } = config;

  const response = await makeRequest({
    method: 'GET',
    apiUrl,
    userId,
    env,
    currentSession,
    route: 'postSeasonLeaderboard',
    // TODO: REMOVE WHEN BOOM GOES LIVE
    // ...(env === 'staging' && { headers: { mockresponse: 'current-postseason' } }),
  });

  return response.status === 'current';
};

export const setCompletedOnboarding = (config) => (dispatch) => {
  const { platform, env } = config;
  const { setValue } = commonActions('completed_onboarding');
  dispatch(setValue());
  // Fire mParticle event, this will trigger Braze Onboarding Modal
  // We want to trigger it here, since there are two in-repo onboarding
  // screens between the STQ and the Braze message this triggers
  fireMessageEvent({
    platform,
    env,
    title: skillTestAnswered,
    data: { path: path, element_name: `${answerElement}_${correctValue}`, element_action: correctValue },
  });
};

export const setSelectedAnswer = (answer) => (dispatch) => {
  const { setValue } = commonActions('selected_answer');
  dispatch(setValue(answer));
};

export const setCorrectAnswer = () => (dispatch) => {
  const { setValue } = commonActions('correct_answer');
  dispatch(setValue());
};

export const setIncorrectAnswer = () => (dispatch) => {
  const { setValue } = commonActions('incorrect_answer');
  dispatch(setValue());
};

export const setLockout = () => (dispatch) => {
  const { setValue } = commonActions('lockout');
  dispatch(setValue());
};

export const setNotificationStatus = (status) => (dispatch) => {
  const { setValue } = commonActions('notifications_status');
  dispatch(setValue(status));
};

export const setNotificationToggle = (status) => (dispatch) => {
  const { setValue } = commonActions('toggle');
  dispatch(setValue(status));
};

export const fetchQuizData = () => async (dispatch, getState) => {
  const { config } = getState();
  const { apiUrl, env, userId, currentSession } = config;
  const { fetchStart, fetchSuccess, fetchFailure } = apiActions('quiz_data');

  dispatch(fetchStart());

  try {
    const isPlayoffs = await checkIfPlayoffs(config);
    dispatch({ type: 'SET_IS_PLAYOFFS', payload: isPlayoffs });
    const eventId = isPlayoffs ? '2025-26-playoffs' : '2025-26';

    const result = await makeRequest({
      method: 'GET',
      route: 'quiz',
      params: { eventId },
      apiUrl,
      env,
      userId,
      currentSession,
    });

    if (result === 'error') {
      return dispatch(fetchFailure('error'));
    }
    return dispatch(fetchSuccess(result));
  } catch (error) {
    if (error.code === 'NotAllowed') {
      return dispatch(setLockout());
    }
    return dispatch(fetchFailure(error));
  }
};

export const sendQuizAnswer = () => async (dispatch, getState) => {
  const { config, onboarding } = getState();
  const { selectedAnswer, attempts, answers } = onboarding;
  const { apiUrl, env, userId, currentSession, platform } = config;

  const { sendStart, sendSuccess, sendFailure } = apiActions('quiz_answer');

  dispatch(sendStart());

  try {
    const isPlayoffs = await checkIfPlayoffs(config);
    const eventId = isPlayoffs ? '2025-26-playoffs' : '2025-26';

    const isIncorrect = selectedAnswer !== answers[attempts];

    const result = await makeRequest({
      route: 'quiz',
      data: { eventId, failed: isIncorrect },
      apiUrl,
      env,
      userId,
      currentSession,
    });

    if (!result) {
      fireMessageEvent({
        platform,
        env,
        title: skillTestAnswered,
        data: { path: path, element_name: `${answerElement}_${incorrectValue}`, element_action: incorrectValue },
      });
      dispatch(setNotificationBarToOpen());
      return dispatch(setIncorrectAnswer());
    }

    fireMessageEvent({
      platform,
      env,
      title: skillTestAnswered,
      data: { path: path, element_name: `${answerElement}_${correctValue}`, element_action: correctValue },
    });

    dispatch(setCorrectAnswer());
    return dispatch(sendSuccess(result));
  } catch (error) {
    if (error.code === 'NotAllowed') {
      return dispatch(setLockout());
    }
    return dispatch(sendFailure(error));
  }
};

export const resetUserNameStatus = () => {
  return { type: 'RESET_USERNAME_STATUS' };
};
export const resetErrorCodeStatus = () => {
  return { type: 'SEND_ERROR_CODE_RESET' };
};

export const resetUsernameAttemptFailure = () => {
  return { type: 'SET_USERNAME_ATTEMPT_FAILURE' };
};

export const resetUsernameAttemptSuccess = () => {
  return { type: 'SET_USERNAME_ATTEMPT_SUCCESS' };
};

export const submitUsername = (username) => async (dispatch, getState) => {
  dispatch(setUsernameValidationErr(false));
  const { config } = getState();
  const { apiUrl, env, userId, currentSession } = config;

  const trimmedUsername = username.trim();
  const regex = new RegExp(/^\w{3,15}$/i);
  if (!regex.test(trimmedUsername)) {
    return dispatch(setUsernameValidationErr(true));
  }

  const { sendStart, sendSuccess, sendFailure } = apiActions('username');
  const { fetchFailure } = apiActions('auth');
  dispatch(sendStart());

  try {
    const result = await makeRequest({
      method: 'PATCH',
      route: 'user',
      data: { username: trimmedUsername },
      apiUrl,
      env,
      userId,
      currentSession,
    });
    dispatch(fetchUserData());

    setTimeout(() => {
      dispatch(resetUserNameStatus());
    }, 10000);

    return batch(() => {
      dispatch(sendSuccess(result));
      dispatch(resetUsernameAttemptSuccess());
    });
  } catch (error) {
    if (error.code && error.code.includes('hc.')) {
      return dispatch(sendFailure(error.code));
    }
    if (error.status === 401) {
      return batch(() => {
        dispatch(fetchFailure());
        dispatch(resetUsernameAttemptFailure());
      });
    }
    return dispatch(sendFailure(error));
  }
};

export const fetchUserData = () => async (dispatch, getState) => {
  const { config } = getState();
  const { apiUrl, env, userId, currentSession } = config;

  const { fetchStart, fetchSuccess, fetchFailure } = apiActions('user');

  dispatch(fetchStart());

  try {
    const result = await makeRequest({
      method: 'GET',
      route: 'user',
      apiUrl,
      env,
      userId,
      currentSession,
    });

    return dispatch(fetchSuccess(result));
  } catch (error) {
    return dispatch(fetchFailure(error));
  }
};

export const setUsername = (username) => (dispatch) => {
  const { setValue } = commonActions('username');
  dispatch(setValue(username));
};

export const setUsernameValidationErr = (isErr) => (dispatch) => {
  const { setValue } = commonActions('username_validation_err');
  dispatch(setValue(isErr));
};

export const fetchNotificationStatus = () => async (dispatch, getState) => {
  const { config } = getState();
  const { apiUrl, env, userId, currentSession } = config;
  const { fetchStart, fetchSuccess, fetchFailure } = apiActions('notifications');

  dispatch(fetchStart());

  try {
    const result = await makeRequest({
      route: 'notification',
      apiUrl,
      env,
      userId,
      currentSession,
    });

    return dispatch(fetchSuccess(result));
  } catch (error) {
    return dispatch(fetchFailure(error));
  }
};

export const sendNotificationStatus = (status) => async (dispatch, getState) => {
  const { config } = getState();
  const { apiUrl, env, userId, currentSession } = config;

  const { sendStart, sendSuccess, sendFailure } = apiActions('notifications');

  dispatch(sendStart());

  try {
    await makeRequest({
      route: 'notification',
      data: {
        attributes: { tnhc_user_reminders: status },
      },
      apiUrl,
      env,
      userId,
      currentSession,
    });

    return dispatch(sendSuccess(status));
  } catch (error) {
    return dispatch(sendFailure(error));
  }
};
